import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/transfert/chauffeur-transfert.png";
import { cityPriceFaq } from "../slugs/cityPricing";

const toulonEn: ChauffeurPage = {
  slug: "toulon",
  title: "Private chauffeur & VTC in Toulon",
  subtitle:
    "Private chauffeur in Toulon: TGV station, harbour and roadstead, Mont Faron and transfers to Toulon-Hyères and Nice airports.",
  keywords: [
    "private chauffeur toulon",
    "vtc toulon",
    "toulon station transfer",
    "chauffeur toulon airport",
  ],
  hero: {
    title: "Private chauffeur & VTC in Toulon",
    subtitle:
      "The roadstead, the naval port, the TGV station and Mont Faron: a chauffeur for the city and the whole Var, around the clock.",
    keywords: [
      "VTC Toulon",
      "Toulon station chauffeur",
      "Toulon-Hyères airport transfer",
      "Toulon roadstead chauffeur",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Private chauffeur & VTC in Toulon",
    badges: [
      "Toulon TGV station",
      "Toulon-Hyères airport ~30 min",
      "FR/EN chauffeurs",
    ],
  },
  benefits: {
    heading: "Why a chauffeur in Toulon",
    items: [
      {
        title: "TGV station in the city centre",
        description:
          "Pick-up at Toulon station, TGV to Paris and connections, with a welcome on the platform or the forecourt.",
      },
      {
        title: "Two airports served",
        description:
          "Toulon-Hyères at around 30 min, Nice Côte d'Azur at close to 1h30: the choice follows your flight.",
      },
      {
        title: "Roadstead & harbour",
        description:
          "Drop-off at the port, the Corsica ferry terminals and points around the roadstead, with no parking.",
      },
      {
        title: "Rated 5.0/5",
        description:
          "Discreet, punctual chauffeurs based on the French Riviera, rated 5.0/5 from 38 Google reviews.",
      },
    ],
  },
  services: {
    heading: "Our chauffeur services in Toulon",
    services: [
      {
        title: "Toulon TGV station transfer",
        text: "Welcome at Toulon station for TGV trains to Paris and connections, departure set to your train.",
      },
      {
        title: "Toulon-Hyères airport transfer",
        text: "The nearest airport, around 30 min away, with flight tracking to and from Toulon.",
      },
      {
        title: "Nice airport transfer",
        text: "Link to Nice Côte d'Azur in close to 1h30 by the A57, in a sedan or premium van.",
      },
      {
        title: "Harbour & roadstead ferries",
        text: "Drop-off at the ferry terminals for Corsica and Sardinia, and at the quays of the Toulon roadstead.",
      },
      {
        title: "Hourly hire & long distance",
        text: "A chauffeur by the day for the city and the Var, or runs to Saint-Tropez, Cannes and Italy.",
      },
    ],
  },
  locations: {
    heading: "Places & pick-up points",
    items: [
      {
        name: "Toulon station",
        description: "TGV station and forecourt, in the city centre.",
      },
      {
        name: "Harbour & roadstead",
        description: "Quays, ferry terminals and the seafront.",
      },
      {
        name: "Mont Faron cable car",
        description: "Cable car station and belvedere over the roadstead.",
      },
      {
        name: "Opera & Cours Lafayette",
        description:
          "Toulon Opera and the Provençal market of Cours Lafayette.",
      },
      {
        name: "Toulon-Hyères airport",
        description: "The nearest airport, around 30 min away.",
      },
      {
        name: "Le Mourillon",
        description: "Beaches and maritime district of the city.",
      },
    ],
  },
  generic: {
    heading: "Toulon, the great roadstead and its TGV station",
    subheading:
      "A dense port city, where the station, the harbour and the heights connect better with a chauffeur.",
    paragraphs: [
      "Toulon is built around one of the finest natural harbours in Europe and its naval port, France's leading naval base. The city is dense: between the station, the harbour, the Opera, the Cours Lafayette market and the beaches of Le Mourillon, a private chauffeur drives you door to door and spares you the tricky parking of the centre.",
      "Toulon station puts Paris within TGV reach: we welcome you there, on the platform or the forecourt, and set the departure to your train. On the flight side, Toulon-Hyères airport is the nearest at around 30 minutes, while Nice Côte d'Azur is reached in close to 1h30 by motorway for international flights.",
      "Toulon is also a departure point for Corsica and Sardinia, and the foot of Mont Faron, whose cable car overlooks the roadstead. For your days, hourly hire entrusts a dedicated chauffeur to the city as much as the rest of the Var, with fixed fares set in advance and an instant quote.",
    ],
    bullets: [
      "Welcome at the TGV station",
      "Two airports & ferries",
      "Fixed fares & instant quote",
    ],
  },
  process: {
    heading: "Book a chauffeur in Toulon",
    steps: [
      "Tell us your train, your flight or your address in Toulon.",
      "We advise the vehicle to suit the luggage, the group and the distance.",
      "The chauffeur tracks the train or the flight and adapts the departure to traffic.",
      "Direct transfer or hourly hire for the city and the roadstead.",
    ],
  },
  faq: [
    cityPriceFaq("en", "Toulon"),
    {
      q: "Do you handle transfers from Toulon TGV station?",
      a: "Yes, it is one of our regular journeys: a welcome at Toulon station for the TGV to Paris and connections, with the departure adjusted to your train time.",
    },
    {
      q: "Which airport is closest to Toulon?",
      a: "Toulon-Hyères airport, around 30 minutes away, is the nearest. Nice Côte d'Azur, larger for long-haul flights, is close to 1h30 by the A57.",
    },
    {
      q: "How far in advance should I book?",
      a: "For a transfer timed to a TGV or a flight, book the day before ideally; we also handle last-minute requests subject to availability.",
    },
    {
      q: "Do your chauffeurs speak English?",
      a: "Yes, our chauffeurs are bilingual French/English, at ease with business and international travellers.",
    },
    {
      q: "Which vehicles do you offer?",
      a: "A Mercedes E-Class or S-Class sedan, a premium 7-seater van for groups and luggage, plus SUV and VIP minibus to suit your needs.",
    },
    {
      q: "Do you serve the harbour and the ferries to Corsica?",
      a: "Yes, we drop off at the ferry terminals of the Toulon roadstead for crossings to Corsica and Sardinia, and collect on the boats' arrival.",
    },
    {
      q: "Do you cover long distances from Toulon?",
      a: "Yes: Saint-Tropez, Cannes, Nice, Monaco, Marseille and Italy, as well as runs from Cannes or Nice to Toulon, in a comfortable vehicle with a dedicated chauffeur.",
    },
  ],
  relatedLinks: [
    {
      label: "French Riviera rates and routes",
      href: "/en/chauffeur/french-riviera",
    },
    { label: "Nice chauffeur rates", href: "/en/chauffeur/nice" },
    { label: "Private car service in Cannes", href: "/en/chauffeur/cannes" },
    {
      label: "Book a chauffeur in Saint-Tropez",
      href: "/en/chauffeur/saint-tropez",
    },
  ],
  cta: {
    title: "Your private chauffeur in Toulon",
    text: "TGV station, airport, harbour or a day around the roadstead: book your chauffeur, quick reply 7 days a week.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book / request a quote",
  },
  seo: {
    title: "Private chauffeur in Toulon — car service & airport transfer",
    description:
      "Private car service in Toulon: TGV station, transfer to Toulon-Hyères (~30 min) and Nice airports, harbour and roadstead, Mont Faron.",
    canonical: "/en/chauffeur/toulon",
  },
};

export default toulonEn;
